import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/App';

const NotificationContext = createContext(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};

export function NotificationProvider({ children, token, userId }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get('/notifications?limit=20');
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws?token=${token}`);

    ws.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'notification') {
          setNotifications(prev => [data.payload, ...prev.slice(0, 19)]);
          setUnreadCount(prev => prev + 1);
          
          // Show browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification('Efacilicare', {
              body: getNotificationText(data.payload),
              icon: '/favicon.ico'
            });
          }
        } else if (data.type === 'ticket_cancelled' || data.type === 'ticket_deleted') {
          // Broadcast so any ticket list / technician view can drop the row
          // without a manual refresh.
          try {
            window.dispatchEvent(new CustomEvent(`efacilicare:${data.type}`, { detail: data.payload || {} }));
          } catch (e) {
            /* noop */
          }
        }
      } catch (e) {
        console.error('WebSocket message parse error:', e);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
      // Attempt reconnect after 5 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, 5000);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    wsRef.current = ws;

    // Keep connection alive with ping
    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send('ping');
      }
    }, 30000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchNotifications();
      connectWebSocket();

      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [token, fetchNotifications, connectWebSocket]);

  const markAsRead = useCallback(async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      connected,
      markAsRead,
      markAllAsRead,
      refresh: fetchNotifications
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function getNotificationText(notification) {
  const { type, data } = notification;
  switch (type) {
    case 'ticket_created':
      return `New ticket created: ${data.job_number}`;
    case 'ticket_escalated':
      return `Ticket ${data.job_number} escalated to Help Desk`;
    case 'ticket_assigned':
      return `Ticket ${data.job_number} assigned to you (${data.priority})`;
    case 'ticket_status_update':
      return `Ticket ${data.job_number}: ${data.new_status}`;
    case 'approval_required':
      return `Approval needed: ${data.job_number} ($${data.total_cost?.toFixed(2)})`;
    case 'ticket_rejected':
      return `Ticket ${data.job_number} rejected: ${data.reason}`;
    case 'repair_authorized':
      return `Repair authorized for ${data.job_number}`;
    case 'ticket_resolved':
      return `Ticket ${data.job_number} resolved`;
    case 'ticket_reopened':
      return `Ticket ${data.parent_job_number} reopened as ${data.job_number}`;
    case 'ticket_cancelled':
      return `Ticket ${data.job_number} was cancelled${data.reason ? `: ${data.reason}` : ''}`;
    case 'new_user_registration':
      return `New user registration: ${data.user_name}`;
    default:
      return 'New notification';
  }
}

export function getNotificationIcon(type) {
  switch (type) {
    case 'ticket_created':
    case 'ticket_escalated':
      return 'ticket';
    case 'ticket_assigned':
      return 'user-check';
    case 'approval_required':
      return 'clock';
    case 'ticket_rejected':
      return 'x-circle';
    case 'repair_authorized':
    case 'ticket_resolved':
      return 'check-circle';
    case 'new_user_registration':
      return 'user-plus';
    default:
      return 'bell';
  }
}
